import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetChat } from "~/api/chat/chat";
import {
  getGetCustomReportQueryKey,
  getListCustomReportsByCaseQueryKey,
  useCreateCustomReport,
  useGetCustomReport,
  useGetTemplateList,
  useListCustomReportsByCase,
  useRegenerateSection,
  useUpdateReportSection,
} from "~/api/custom-reports/custom-reports";
import { Report, ReportListItem } from "~/api/model";
import { TemplateListItem } from "~/api/model/templateListItem";
import { useReportStore } from "../../stores/reportStore";
import { useTermsStore } from "../../stores/termsStore";

/**
 * Extended Report type that includes the optional hint field for term highlighting.
 * This type is used to handle the schema mismatch during the transition period
 * when the backend has the hint field but the dev specs haven't been updated yet.
 * Once the backend PR is deployed, the generated Report type will include hint natively.
 */
type ReportWithHint = Report & {
  hint?: {
    term: string;
    definition: string;
  }[];
};

export type TemplateReport = {
  template: TemplateListItem | null;
  report: ReportListItem | null;
};

export const useReportData = (
  organizationId: string,
  caseId: string,
  chatId: string
) => {
  const queryClient = useQueryClient();
  const selectedReportIndex = useReportStore((s) =>
    s.getSelectedReportIndex(chatId)
  );
  const setSelectedReportIndexInStore = useReportStore(
    (s) => s.setSelectedReportIndex
  );
  const setSelectedReportIndex = (index: number) =>
    setSelectedReportIndexInStore(chatId, index);

  const [locallyGenerating, setLocallyGenerating] = useState<Set<string>>(
    new Set()
  );

  const { data: chat } = useGetChat(organizationId, caseId, chatId, {
    query: { staleTime: Infinity },
  });

  const { data: templatesData, isLoading: isTemplatesLoading } =
    useGetTemplateList(organizationId, {
      query: { staleTime: 30_000 },
    });

  const { data: reportsData, isLoading: isReportsLoading } =
    useListCustomReportsByCase(organizationId, caseId, {
      query: {
        refetchInterval: (query) => {
          if (!query.state.data) {
            return false;
          }

          const reports = query.state.data.reportList ?? [];
          const needsPolling = reports.some(
            (report) =>
              report.status !== "completed" &&
              report.status !== "failed" &&
              report.status !== "outdatedByDoclist" &&
              report.status !== "outdatedByTemplate"
          );

          return needsPolling ? 15_000 : false;
        },
        refetchIntervalInBackground: true, // Continue polling even when tab is not active
      },
    });

  // Track previous statuses to detect transitions to "completed"
  const prevStatusesRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const list = reportsData?.reportList ?? [];
    const prev = prevStatusesRef.current;
    const next: Record<string, string> = {};

    for (const r of list) {
      next[r.id] = r.status;
      const finalStatuses = [
        "completed",
        "outdatedByDoclist",
        "outdatedByTemplate",
        "failed",
      ];
      const isFinalStatus = finalStatuses.includes(r.status);
      const wasFinalStatus = prev[r.id] && finalStatuses.includes(prev[r.id]);

      if (isFinalStatus && prev[r.id] && !wasFinalStatus) {
        queryClient.invalidateQueries({
          queryKey: getGetCustomReportQueryKey(organizationId, r.id),
        });
      }
    }

    prevStatusesRef.current = next;
  }, [organizationId, reportsData?.reportList, queryClient]);

  const templateReports = useMemo<TemplateReport[]>(() => {
    const templates = templatesData?.templateList ?? [];

    const result: TemplateReport[] = templates
      .filter((template) => !template.isDraft)
      .sort((a, b) => {
        // Sort by isDefault: default template first
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return 0;
      })
      .map((template) => ({
        template,
        report: null,
      }));

    const reports = reportsData?.reportList ?? [];

    reports.forEach((report) => {
      const existingTempRep = result.find(
        (templateReport) => templateReport.template?.name === report.title
      );

      if (existingTempRep) {
        existingTempRep.report = report;
      } else {
        result.push({
          template: null,
          report,
        });
      }
    });

    return result;
  }, [templatesData, reportsData]);

  const selectedReport =
    templateReports.length > selectedReportIndex
      ? templateReports[selectedReportIndex].report
      : null;

  const reportId = selectedReport?.id ?? "";

  const { data: reportData, isLoading: isReportContentLoading } =
    useGetCustomReport(organizationId, reportId, {
      query: {
        enabled: !!reportId,
        refetchInterval: (query) => {
          if (!query.state.data) {
            return false;
          }

          const sections = query.state.data.reportSections ?? [];
          const needsPolling = sections.some(
            (section) =>
              section.status !== "completed" && section.status !== "failed"
          );

          return needsPolling ? 3000 : false;
        },
        refetchIntervalInBackground: true,
      },
    });

  // Add terms from report hint to the terms store for term highlighting
  const addTerms = useTermsStore((s) => s.addTerms);
  useEffect(() => {
    if (!reportData) return;

    // Cast to ReportWithHint to access the hint field during the transition period
    const reportWithHint = reportData as ReportWithHint;
    const hint = reportWithHint.hint;

    if (!hint || hint.length === 0) return;

    addTerms(caseId, hint);
  }, [reportData, caseId, addTerms]);

  const { mutate: regenerateSection } = useRegenerateSection({
    mutation: {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches to avoid overwriting our optimistic update
        await queryClient.cancelQueries({
          queryKey: getGetCustomReportQueryKey(organizationId, reportId),
        });

        // Snapshot the previous value for rollback
        const previousReport = queryClient.getQueryData(
          getGetCustomReportQueryKey(organizationId, reportId)
        );

        // Optimistically update the section status to pending
        queryClient.setQueryData(
          getGetCustomReportQueryKey(organizationId, reportId),
          (old: any) => {
            if (!old?.reportSections) return old;

            return {
              ...old,
              reportSections: old.reportSections.map((section: any) =>
                section.id === variables.reportSectionId
                  ? { ...section, status: "pending" }
                  : section
              ),
            };
          }
        );

        // Return context with previous data for rollback
        return { previousReport };
      },
      onError: (_err, _variables, context) => {
        // Rollback to previous data on error
        if (context?.previousReport) {
          queryClient.setQueryData(
            getGetCustomReportQueryKey(organizationId, reportId),
            context.previousReport
          );
        }
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetCustomReportQueryKey(organizationId, reportId),
          }),
          queryClient.invalidateQueries({
            queryKey: getListCustomReportsByCaseQueryKey(
              organizationId,
              caseId
            ),
          }),
        ]);
      },
    },
  });

  const { mutate: createReport } = useCreateCustomReport({
    mutation: {
      onSuccess: async (_data, _variables) => {
        // Don't clear locallyGenerating here - let the effect below handle it
        // when the report reaches a final status
        await queryClient.resetQueries({
          queryKey: getListCustomReportsByCaseQueryKey(organizationId, caseId),
        });
      },
      onError: (_error, variables) => {
        // Clear the locally generating state on error as well
        setLocallyGenerating((prev) => {
          const next = new Set(prev);
          next.delete(variables.data.templateId);
          return next;
        });
      },
    },
  });

  // Clean up locallyGenerating when reports reach final status
  useEffect(() => {
    const reports = reportsData?.reportList ?? [];
    const templates = templatesData?.templateList ?? [];

    const finalStatuses = [
      "completed",
      "outdatedByDoclist",
      "outdatedByTemplate",
      "failed",
    ];

    setLocallyGenerating((prev) => {
      const next = new Set(prev);
      let changed = false;

      // For each template we're tracking as locally generating
      prev.forEach((templateId) => {
        // Find the template
        const template = templates.find((t) => t.id === templateId);
        if (!template) return;

        // Find the corresponding report by matching template name with report title
        const report = reports.find((r) => r.title === template.name);

        // If report exists and is in a final status, remove from locallyGenerating
        if (report && finalStatuses.includes(report.status)) {
          next.delete(templateId);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [reportsData?.reportList, templatesData?.templateList]);

  const { mutate: updateReportSection } = useUpdateReportSection({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getGetCustomReportQueryKey(organizationId, reportId),
        });
      },
    },
  });

  const handleGenerateReport = useCallback(
    (templateId: string) => {
      if (!templateId) return;
      setLocallyGenerating((prev) => new Set(prev).add(templateId));
      createReport({
        organizationId,
        data: {
          caseId,
          idempotencyKey:
            typeof crypto !== "undefined" &&
            typeof (crypto as any).randomUUID === "function"
              ? (crypto as any).randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          templateId,
        },
      });
    },
    [createReport, organizationId, caseId]
  );

  const handleRegenerateSection = useCallback(
    (reportSectionId: string) => {
      if (!reportSectionId) return;
      regenerateSection({
        organizationId,
        reportSectionId,
        data: {
          caseId,
          idempotencyKey:
            typeof crypto !== "undefined" &&
            typeof (crypto as any).randomUUID === "function"
              ? (crypto as any).randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        },
      });
    },
    [regenerateSection, organizationId, caseId]
  );

  const handleEditSection = useCallback(
    async (reportSectionId: string, content: string): Promise<void> => {
      if (!reportSectionId) return;
      return new Promise((resolve, reject) => {
        updateReportSection(
          {
            organizationId,
            reportSectionId,
            data: {
              content,
            },
          },
          {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          }
        );
      });
    },
    [updateReportSection, organizationId]
  );

  const content = reportData?.text ?? "";

  return {
    content,
    locallyGenerating,
    handleGenerateReport,
    handleRegenerateSection,
    handleEditSection,
    templateReports,
    isLoading:
      isTemplatesLoading ||
      isReportsLoading ||
      (!!reportId && isReportContentLoading),
    selectedReportIndex,
    setSelectedReportIndex,
    chatTitle: chat?.title ?? "",
    reportSections: reportData?.reportSections,
  };
};
